import {
  useFormattedFuels,
  useFormattedSites,
  useFormattedSources,
  useGetPeriodOptions,
  useUnitOptions,
} from '@app/ccf/company-report/utils';
import { useGetSites } from '@shared/api';
import { useGetFuels } from '@shared/api/hooks/ccf/fuels';
import { useGetGases } from '@shared/api/hooks/ccf/gases';
import { useGetCompanyReportPeriods } from '@shared/api/hooks/ccf/periods';
import { useGetActivityTypeUnits } from '@shared/api/hooks/ccf/units';
import { useMemo } from 'react';
import { Field, Fields } from 'react-spreadsheet-import/types/types';
import { Scope1And2CategoryConfig } from './config';

const maxLengthValidation = (length: number) => ({
  rule: 'regex' as const,
  value: `^.{0,${length}}$`,
  errorMessage: `Must not be longer than ${length} characters`,
  level: 'error' as const,
});

const requiredValidation = (name: string) => ({
  rule: 'required' as const,
  errorMessage: `${name} is required`,
  level: 'error' as const,
});

export interface Scope1And2FieldOptions {
  /** When a single site is chosen up-front, the Site column is excluded. */
  excludeSite?: boolean;
  /** When a single period is chosen up-front, the Time period column is excluded. */
  excludePeriod?: boolean;
}

/**
 * Builds the spreadsheet importer fields for a Scope 1/2 category. Reference
 * data (sites, fuels, gases, units, time periods) is fed in as select options so
 * the importer resolves the user's typed names to backend UUIDs via
 * `autoMapSelectValues`. Units are scoped to the category's activity type and
 * mobile-combustion fuels are filtered to `include_mobile_combustion`, mirroring
 * the backend serializer constraints. Time periods are resolved against the
 * report's own periods (not the organisation's) so imported rows land in the
 * report's reporting window — labels outside it won't map and are rejected.
 */
export const useScope1And2Fields = (
  config: Scope1And2CategoryConfig,
  companyReportUuid: string,
  options: Scope1And2FieldOptions = {}
): Fields<string> => {
  const { data: sitesData } = useGetSites({ page: 1, page_size: 1000 });
  const formattedSites = useFormattedSites(sitesData?.results || []);

  const { data: fuels } = useGetFuels(
    config.mobileFuelsOnly ? { include_mobile_combustion: true } : undefined,
    { enabled: config.hasFuel }
  );
  const formattedFuels = useFormattedFuels(fuels || []);

  const { data: gases } = useGetGases(undefined, { enabled: config.hasGas });
  const formattedGases = useMemo(
    () => (gases || []).map((gas) => ({ label: gas.name, value: gas.uuid })),
    [gases]
  );

  const { data: units } = useGetActivityTypeUnits(config.slug);
  const unitOptions = useUnitOptions(units || []);

  const { data: periods } = useGetCompanyReportPeriods(companyReportUuid);
  const periodOptions = useGetPeriodOptions(periods || []);

  const sourceOptions = useFormattedSources();

  const fields: Field<string>[] = [];

  if (!options.excludeSite) {
    fields.push({
      label: 'Site',
      key: 'site',
      alternateMatches: ['location', 'place'],
      fieldType: { type: 'select', options: formattedSites },
      example: 'Liverpool',
      validations: config.siteOptional ? [] : [requiredValidation('Site')],
    });
  }

  if (config.hasSource) {
    fields.push({
      label: 'Source',
      key: 'source',
      alternateMatches: ['gas source'],
      fieldType: { type: 'select', options: sourceOptions },
      example: 'Air conditioning',
      validations: [requiredValidation('Source')],
    });
  }

  if (config.hasGas) {
    fields.push({
      label: 'Gas',
      key: 'gas',
      alternateMatches: ['refrigerant'],
      fieldType: { type: 'select', options: formattedGases },
      example: 'R404A',
      validations: [requiredValidation('Gas')],
    });
  }

  if (config.hasFuel) {
    fields.push({
      label: 'Fuel',
      key: 'fuel',
      alternateMatches: ['fuel type'],
      fieldType: { type: 'select', options: formattedFuels },
      example: 'Natural gas',
      validations: [requiredValidation('Fuel')],
    });
  }

  fields.push({
    label: 'Unit',
    key: 'unit',
    alternateMatches: ['uom', 'units'],
    fieldType: { type: 'select', options: unitOptions },
    example: 'kWh',
    validations: [requiredValidation('Unit')],
  });

  if (!options.excludePeriod) {
    fields.push({
      label: 'Time period',
      key: 'timePeriod',
      alternateMatches: [
        'month/full year',
        'period',
        'time period',
        'month',
        'year',
      ],
      fieldType: { type: 'select', options: periodOptions },
      example: 'FY23',
      validations: [requiredValidation('Time period')],
    });
  }

  fields.push({
    label: 'Quantity',
    key: 'quantity',
    alternateMatches: ['qty', 'amount', 'consumption', 'value'],
    fieldType: { type: 'input' },
    example: '1500',
    validations: [requiredValidation('Quantity'), maxLengthValidation(36)],
  });

  return fields;
};
